import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import config from 'constants/config'

type OfferTypeItem = App.AppConfig['offerTypesList'][number]

function useLocalizedOfferTypesList(): Array<OfferTypeItem> {
  const intl = useIntl()

  return useMemo(() => {
    const localizedByPathPrefix: Record<string, { title: string; description: string }> = {
      '/hotels': {
        title: intl.formatMessage({
          id: 'VTpyxYqQo_WZf6ig1zRK',
          defaultMessage: 'Hotels',
          description: 'Title label for the hotels offer type in the localised offer types list display',
        }),
        description: intl.formatMessage({
          id: 'c6u04c71dxl7ZWjXKrIR',
          defaultMessage: 'The best hotels and resorts in the world',
          description: 'Offer type description for hotels on the hotel or room booking page',
        }),
      },
      '/tours': {
        title: intl.formatMessage({
          id: 'a8n65pmkTGJ7TBe3Sp70',
          defaultMessage: 'Tours',
          description: 'Title label for the tours offer type in the localised offer types list display',
        }),
        description: intl.formatMessage({
          id: '_2Zb8fHVE24pMXlik_6g',
          defaultMessage: 'Tours that do all the hard work for you',
          description: 'Offer type description for tours on the tour offer detail page',
        }),
      },
      '/cruises': {
        title: intl.formatMessage({
          id: 'Oxjkf_auVs15_dWac_Y0',
          defaultMessage: 'Cruises',
          description: 'Title label for the cruises offer type in the localised offer types list display',
        }),
        description: intl.formatMessage({
          id: 'anZjFspIz7SyoM7a2hrW',
          defaultMessage: 'Thousands of itineraries all around the world',
          description: 'Offer type description for cruises in the cruise offer or booking section',
        }),
      },
      '/ultra-lux': {
        title: intl.formatMessage({
          id: 'qXERclRl4lc66dLv-Xqg',
          defaultMessage: 'Ultra Lux',
          description: 'Offer type title for ultra lux on the offer listing or detail page',
        }),
        description: intl.formatMessage({
          id: 'SfSnXMfoSmjlWsjGYLrY',
          defaultMessage: 'Experience the world’s most luxurious hotels, tours & cruises',
          description: 'Offer type description for ultra lux in the cruise offer or booking section',
        }),
      },
      '/search/homes-and-villas': {
        title: intl.formatMessage({
          id: '-Atrwzu3_j--mQ2JIDMf',
          defaultMessage: 'Homes & Villas',
          description: 'Offer type title for homes and villas on the villa or rental property page',
        }),
        description: intl.formatMessage({
          id: 'BpAQ5EPZGTaVwvdU74Fa',
          defaultMessage: 'Entire properties for the ultimate escape',
          description: 'Offer type description for homes and villas on the rental page',
        }),
      },
      '/experiences': {
        title: intl.formatMessage({
          id: 'W3h42tf8zuZwxfONuafz',
          defaultMessage: 'Experiences',
          description: 'Offer type title for experiences on the experience offer or booking page',
        }),
        description: intl.formatMessage({
          id: 'qaujMTU7Ark_SzbLHxEW',
          defaultMessage: 'Immerse yourself in your destination',
          description: 'Offer type description for experiences on the experience offer or booking page',
        }),
      },
      '/flights': {
        title: intl.formatMessage({
          id: 'ep6bplwWi4B0hsT7ZLqd',
          defaultMessage: 'Flights',
          description: 'Title label for the flights offer type in the localised offer types list display',
        }),
        description: intl.formatMessage({
          id: 'FCqM3fXZTW3jnB62P2v-',
          defaultMessage: 'Your ticket to the world',
          description: 'Offer type description for flights in the flight search or booking flow',
        }),
      },
      '/car-hire': {
        title: intl.formatMessage({
          id: 'jM3c5eS3RS8io2BEe93X',
          defaultMessage: 'Car Hire',
          description: 'Title label for the car hire offer type in the localised offer types list display',
        }),
        description: intl.formatMessage({
          id: 'BrrigfncI1D4y4ndLETW',
          defaultMessage: 'Making it easy to get from A to B',
          description: 'Offer type description for car hire in the car hire booking section',
        }),
      },
    }

    return config.offerTypesList.map((item) => {
      const match = Object.entries(localizedByPathPrefix).find(([prefix]) => item.to.startsWith(prefix))
      if (match) {
        return { ...item, ...match[1] }
      }
      return item
    })
  }, [intl])
}

export default useLocalizedOfferTypesList
